import {
  Box,
  SimpleGrid,
  Skeleton,
  SkeletonCircle,
  SkeletonText,
  Stack,
  useColorModeValue,
} from '@chakra-ui/react'

interface PageSkeletonProps {
  minHeight?: string | number
  cardCount?: number
  lines?: number
  showHeader?: boolean
}

export function PageSkeleton({
  minHeight = '320px',
  cardCount = 4,
  lines = 6,
  showHeader = true,
}: PageSkeletonProps) {
  return (
    <Box w="full" minH={minHeight} py={4}>
      <Stack spacing={6}>
        {showHeader && (
          <Stack spacing={3}>
            <Skeleton
              height="32px"
              width={{ base: '60%', md: '40%', xl: '30%' }}
            />
            <SkeletonText
              noOfLines={2}
              spacing={3}
            />
          </Stack>
        )}

        <SimpleGrid columns={{ base: 1, md: 2, xl: Math.min(cardCount, 4) }} spacing={4}>
          {Array.from({ length: Math.max(cardCount, 2) }).map((_, index) => (
            <Box key={index} borderRadius="md" overflow="hidden">
              <Skeleton
                height={{ base: '120px', md: '140px' }}
              />
            </Box>
          ))}
        </SimpleGrid>

        <SkeletonText
          noOfLines={lines}
          spacing={4}
          skeletonHeight="18px"
        />
      </Stack>
    </Box>
  )
}

interface SectionSkeletonProps {
  lines?: number
  withTitle?: boolean
  minHeight?: string | number
}

export function SectionSkeleton({
  lines = 5,
  withTitle = true,
  minHeight,
}: SectionSkeletonProps) {
  return (
    <Stack spacing={4} minH={minHeight} w="full">
      {withTitle && (
        <Skeleton
          height="28px"
          width={{ base: '50%', md: '35%' }}
        />
      )}
      <SkeletonText
        noOfLines={lines}
        spacing={3}
        skeletonHeight="16px"
      />
    </Stack>
  )
}

interface TableSkeletonProps {
  rows?: number
  rowHeight?: string | number
  includeHeader?: boolean
}

export function TableSkeleton({ rows = 6, rowHeight = '52px', includeHeader = true }: TableSkeletonProps) {
  const borderColor = useColorModeValue('gray.200', 'gray.700')

  return (
    <Stack spacing={0} borderRadius="lg" overflow="hidden" borderWidth="1px" borderColor={borderColor} w="full">
      {includeHeader && (
        <Box px={6} py={4} borderBottomWidth="1px" borderColor={borderColor}>
          <Skeleton
            height="24px"
            width={{ base: '40%', md: '25%' }}
          />
        </Box>
      )}
      {Array.from({ length: rows }).map((_, index) => (
        <Box
          key={index}
          px={{ base: 4, md: 6 }}
          py={3}
          borderBottomWidth={index === rows - 1 ? 0 : 1}
          borderColor={borderColor}
        >
          <Skeleton
            height={rowHeight}
          />
        </Box>
      ))}
    </Stack>
  )
}

interface InlineSkeletonProps {
  width?: string | number
  height?: string | number
  isCircle?: boolean
}

export function InlineSkeleton({
  width = '16px',
  height = '16px',
  isCircle = false,
}: InlineSkeletonProps) {
  const widthValue = typeof width === 'number' ? `${width}px` : width
  const heightValue = typeof height === 'number' ? `${height}px` : height
  const sizeValue = isCircle
    ? (typeof height === 'undefined' ? widthValue : heightValue)
    : undefined

  if (isCircle) {
    return (
      <SkeletonCircle
        size={sizeValue ?? widthValue}
      />
    )
  }

  return (
    <Skeleton
      width={widthValue}
      height={heightValue}
    />
  )
}
